import { ChangeDetectionStrategy, Component, computed, effect, signal } from '@angular/core';
import { AbstractBasePanelComponent } from '../utils/abstract-base-panel.component';
import { CustomPackageDto, DiscountDto, PackageOptionDto } from '../dto';
import { filter, kebabCase, orderBy, some } from 'lodash';
import { SkeletonScreenComponent } from '../skeleton-screen/skeleton-screen.component';
import { InputPanelComponent } from '../components/input-panel/input-panel.component';
import { PanelComponent } from '../panel/panel.component';
import { CurrencyPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'webmodule-order-panel-television',
  templateUrl: './panel-television.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PanelComponent, InputPanelComponent, SkeletonScreenComponent, CurrencyPipe, TranslatePipe],
  standalone: true,
})
export class PanelTelevisionComponent extends AbstractBasePanelComponent<CustomPackageDto> {
  protected readonly string = String;
  readonly interactiveTv: CustomPackageDto[] = [
    {
      id: 'withoutItv',
      name: 'Nee, ik wil geen interactive TV',
      price: 0,
    },
    {
      id: 'withItv',
      name: 'Ja, ik wil interactive TV',
      price: 0,
    },
  ];
  readonly itvBoxType: CustomPackageDto[] = [
    {
      id: 'withoutItvBox',
      name: 'Geen TV box, alleen via de Multivisie app',
      price: 0,
    },
    {
      id: 'withItvBox',
      name: 'Met interactive tv box(en)',
      price: 0,
    },
  ];
  readonly selectedTvOptions = signal<PackageOptionDto[]>([]);
  readonly selectItvBoxType = signal<string | null>(null);
  readonly selectBoxes = signal<number | null>(null);
  readonly tvExtraOptions = computed(() =>
    orderBy(
      this.packageService.tvExtraOptions().filter((extra) => !extra.tag?.includes('tv_extra_box')),
      'isPartOfPackage',
      'desc',
    ),
  );
  readonly tvBoxOption = computed(() => this.packageService.tvExtraOptions().find((extra) => extra.tag?.includes('tv_extra_box')));
  readonly tvBoxUnitPrice = computed(() => this.tvBoxOption()?.price ?? 0);
  readonly packageWithTv = computed(() => 'all-in-one' === kebabCase(this.packageService.selected()?.type));
  selectTvOptions = new Map<number, PackageOptionDto | null>();

  constructor() {
    super();

    effect(() => {
      if (this.isLoaded() && !this.select()) {
        this.selectItvBoxType.set(null);
        this.selectBoxes.set(null);

        this.setSelected({ id: 'withItv', name: 'Ja, ik wil interactive TV', price: 0 });
      }

      if (this.packageService.selected()) {
        const selectedTvOptionsUpdate = filter(this.tvExtraOptions(), (obj) => some(this.selectedTvOptions(), { id: obj.id }));
        selectedTvOptionsUpdate.forEach((option) => this.setPackageState(option));
      }

      this.tvExtraOptions().forEach((option) => option.isPartOfPackage && this.setPackageState(option));
    });

    effect(() => {
      if (this.orderService.order.tvOptions()) {
        for (const option of this.orderService.order.tvOptions()) {
          this.selectTvOptions.set(option.id, option);
        }
      }
    });
  }

  setType(type: CustomPackageDto) {
    if ('withoutItvBox' === type.id) {
      this.selectBoxes.set(null);
      this.showSelected.set(false);
      this.updateTvBoxPackageValue(0);
    } else {
      this.selectBoxes.set(0);
    }

    this.updateOrder(type, 'itvBoxType');
    this.selectItvBoxType.set(type.id);
  }

  setBoxes(value: string | number) {
    const parsedValue = typeof value === 'number' ? value : parseFloat(value);
    if (typeof value === 'string' || isNaN(parsedValue)) {
      return;
    }

    this.selectBoxes.set(parsedValue);
    this.showSelected.set(false);
    this.updateTvBoxPackageValue(parsedValue);
  }

  getSelectedType() {
    return this.itvBoxType.find((itv) => itv.id === this.selectItvBoxType());
  }

  getSelectedBoxes() {
    return this.selectBoxes() ?? 0;
  }

  override setSelected(value: CustomPackageDto) {
    super.setSelected(value, 'itv');

    this.setType(this.orderService.order.withTvBox ? this.itvBoxType[1] : this.itvBoxType[0]);
  }

  isTvOptionsSelected(id: number) {
    const option = this.orderService.order.tvOptions().find((option) => option.id === id);

    return Boolean(option);
  }

  setPackageState(item: PackageOptionDto) {
    const exist = this.selectTvOptions.get(item.id);

    if (!exist) {
      this.selectTvOptions.set(item.id, item);
    } else {
      const value = exist.id === item.id ? null : item;

      this.selectTvOptions.set(item.id, value);
    }

    this.updateTvOrderState();
  }

  /**
   * If there is a discountOption for tvBox copy it and multiply the amount by the amount of boxes
   *  If index is 0 no discount is applied because the first is always included
   * @param {number} id
   * @param {number} index
   * @returns {DiscountDto | null}
   */
  getTvBoxDiscount(id: number, index: number): DiscountDto | null {
    const hasDiscountOption = this.getDiscountById(id);
    if (!hasDiscountOption || index === 0) {
      return null;
    }

    const tvBoxDiscount = { ...hasDiscountOption };

    if (tvBoxDiscount.amount) {
      tvBoxDiscount.amount = index * tvBoxDiscount.amount;
    }

    return tvBoxDiscount;
  }

  isTvBoxSelected(index: number, value: string): boolean {
    const parsedValue = parseFloat(value);

    if (isNaN(parsedValue)) {
      return false;
    }

    return parsedValue === index;
  }

  private updateTvBoxPackageValue(value: number) {
    const valueAsString = value.toString();

    const tvBoxPackageDto = this.packageService.tvExtraOptions().find((option) => option.tag.includes('tv_extra_box'));
    if (tvBoxPackageDto) {
      tvBoxPackageDto.value = valueAsString;
      this.updateOrder<PackageOptionDto>(tvBoxPackageDto, 'itvBox');
    }
  }

  /**
   * iterate through options and update the orderState
   *  product can be null to remove an option from orderState
   * @private
   */
  private updateTvOrderState(): void {
    const options: PackageOptionDto[] = [];
    for (const [key, product] of this.selectTvOptions.entries()) {
      if (product) {
        product.value = 'true';
        options.push(product);
      }
    }

    this.updateOrder<PackageOptionDto[]>(options.length ? options : null, 'tvOptions');
  }
}
